// Messages/ThreadCard.jsx
// Component for displaying individual message thread

import React, { useState } from 'react';
import { styles } from './styles';

// User Icon SVG
const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

// Clock Icon SVG
const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const ThreadCard = ({ thread, userInfo, currentUserId, onThreadClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const info = userInfo || {};
  
  // Check if this thread has unread messages
  const hasUnreadMessages = () => {
    // If there's a last message and it wasn't sent by current user
    if (thread.lastMessage && thread.lastMessageSender && thread.lastMessageSender !== currentUserId) {
      // Check if current user has read it
      return !thread.readBy || !thread.readBy.includes(currentUserId);
    }
    return false;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp?.toDate) return '';
    const date = timestamp.toDate();
    const now = new Date();
    const diff = now - date;
    
    // Less than 1 minute
    if (diff < 60000) return 'Just now';
    // Less than 1 hour
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    // Less than 24 hours
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    // Less than 7 days
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleCardClick = () => {
    onThreadClick(thread.id);
  };

  return (
    <div
      onClick={handleCardClick}
      style={{
        ...styles.threadCard,
        ...(isHovered ? styles.threadCardHover : {}),
        position: 'relative'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div style={styles.avatarWrapper}>
        {info.profilePic ? (
          <img 
            src={info.profilePic} 
            alt="User avatar" 
            style={styles.avatar}
          />
        ) : (
          <div style={styles.avatarPlaceholder}>
            <UserIcon />
          </div>
        )}
      </div>
      
      <div style={styles.threadInfo}>
        <div style={styles.threadHeader}>
          <div style={styles.userName}>
            {info.name || 'Unknown User'}
          </div>
          <div style={styles.timestamp}>
            <ClockIcon />
            <span>{formatTimestamp(thread.lastUpdated)}</span>
          </div>
        </div>
        
        <div style={styles.lastMessage}>
          {thread.lastMessage || <span style={styles.noMessageText}>No messages yet</span>}
        </div>
      </div>
      
      <div style={styles.chevron}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </div>
      
      {/* Notification dot for unread messages */}
      {hasUnreadMessages() && (
        <div style={{
          position: 'absolute',
          top: '12px',
          right: '12px',
          width: '10px',
          height: '10px',
          backgroundColor: '#22c55e', // Green color for message notifications
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          animation: 'pulse 2s infinite',
          zIndex: 1,
        }} />
      )}
    </div>
  );
};

export default ThreadCard;